import React from 'react';
import RegionExample from '../assets/images/RegionExample.png'
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

function CaptionsPost() {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto p-5">
      <Helmet>
        <title>Textractify | Capture On-Screen Captions with Region Detection</title>
        <meta
          name="description"
          content="Textractify extracts captions directly from videos using OCR and region detection. Capture, refine, and export subtitles as TXT or CSV with timestamps."
        />
        <meta
          property="og:title"
          content="Textractify | Capture On-Screen Captions with Region Detection"
        />
        <meta
          property="og:description"
          content="Extract video captions with OCR and region detection. Export text with timestamps in TXT or CSV for analysis and reuse."
        />
        <meta property="og:image" content={RegionExample} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Textractify | Capture On-Screen Captions with Region Detection"
        />
        <meta
          name="twitter:description"
          content="Use Textractify to extract, refine, and export captions from videos with OCR and region detection."
        />
        <meta name="twitter:image" content={RegionExample} />
      </Helmet>
      <article>
        <header className="mb-8">
          <figure className="mb-4">
            <img
              src={RegionExample}
              alt="captions extraction demo"
              className="w-full h-48 object-cover rounded-xl"
            />
          </figure>
          <h1 className="text-4xl font-bold">
            Capturing On-Screen Captions with Textractify
          </h1>
          <p className="text-gray-500">Published on April 1, 2024</p>
        </header>

        <section className="space-y-6">
          <p className="text-lg">
            In today’s multimedia-driven world, videos are a crucial medium for
            communication, whether for educational content, corporate training,
            or entertainment. However, extracting valuable information from
            on-screen captions—especially when no subtitle file is available—can
            be a frustrating challenge. Textractify.com addresses this problem
            head-on by capturing text directly from video frames and providing
            tools to organize and export it.
          </p>

          <h2 className="text-xl">The Challenge of Embedded Captions</h2>
          <p className="text-lg">
            Many videos, particularly those on platforms like YouTube, may not
            include downloadable caption files or may feature hard-coded captions
            that are not selectable or searchable. This makes accessibility and
            analysis difficult. Textractify’s Optical Character Recognition
            (OCR) technology solves this by detecting and extracting captions
            directly from the screen.
          </p>

          <h2 className="text-xl">Advanced OCR for Caption Capture</h2>
          <p className="text-lg">
            Textractify scans each frame of a video to identify and extract
            displayed text. This process is particularly effective for capturing
            subtitles and captions that would otherwise be locked inside the
            video. By converting them into structured text, users can search,
            analyze, and reuse this information in new ways.
          </p>

          <h2 className="text-xl">Region Detection Mode</h2>
          <p className="text-lg">
            One of Textractify’s most powerful tools is its <b>Region mode</b>.
            Users can draw and resize a bounding box over the caption area of
            the video, ensuring only text inside that region is captured. This
            is ideal for subtitles or recurring text in a consistent location.
            The captured results can be exported as <code>.txt</code> or{' '}
            <code>.csv</code> files, with each row mapped to a timestamp for
            precise analysis.
          </p>

          <h2 className="text-xl">Exporting Results</h2>
          <p className="text-lg">
            Once captions are captured, Textractify makes it simple to export
            results for further use. Exports are available in structured formats
            that integrate easily with spreadsheets or data analysis tools.
            Whether you need a quick plain-text file or a timestamped CSV, your
            data is ready in seconds.
          </p>

          <h2 className="text-xl">Conclusion</h2>
          <p className="text-lg">
            Textractify.com changes how we interact with video content by making
            on-screen captions searchable, analyzable, and exportable. Whether
            you are a content creator looking to archive captions, an educator
            preparing study material, or a researcher conducting detailed
            multimedia analysis, Textractify provides the tools you need to work
            with caption text effectively.
          </p>
          <p className="text-lg">
            Discover how Textractify can enhance your video content analysis by
            visiting our website today. Unlock the full potential of your
            multimedia content with Textractify—where every word on screen is
            captured and ready to use.
          </p>
        </section>
      </article>

      <div className="flex justify-center mt-6">
        <button className="btn btn-sm" onClick={() => navigate('/blog')}>
          Back to all posts
        </button>
      </div>
    </div>
  );
}

export default CaptionsPost;